/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file WorkLocationHost.js
 * @description
 * App-level host of the work-event location pings. Mounted once in `App.jsx` (inside the providers):
 *  - renders the one-time {@link WorkLocationDisclosureSheet} when `workLocation.getQuickFix` needs consent;
 *  - sends the throttled `app_open` ping on cold start after login and on every AppState -> active, and retries the
 *    offline queue on foreground. Gated on login + a selected store (`SystemContext.restaurant`, never the dead
 *    redux `device.restaurant`).
 * Renders nothing else. Every async path is guarded; nothing here can reject unhandled.
 *
 * @module components/workLocation/WorkLocationHost
 */

import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {AppState} from 'react-native';
import {SystemContext} from '../../helpers/SystemContext';
import {flushQueue, sendAppOpenPing, setDisclosureHandler} from '../../helpers/workLocation';
import WorkLocationDisclosureSheet from './WorkLocationDisclosureSheet';

/**
 * @param {{isLoggedIn: boolean}} props Login state from `App.jsx`.
 * @returns {JSX.Element|null} The disclosure sheet (hidden unless consent is being asked).
 */
const WorkLocationHost = ({isLoggedIn}) => {
    const system = useContext(SystemContext);
    const restaurantId = system?.restaurant?._id || system?.restaurant?.id || '';
    const [visible, setVisible] = useState(false);
    const resolver = useRef(null);

    const settle = useCallback((allowed) => {
        const resolve = resolver.current;

        resolver.current = null;
        setVisible(false);
        resolve?.(allowed);
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setDisclosureHandler(null);

            return undefined;
        }

        setDisclosureHandler(() => new Promise((resolve) => {
            resolver.current?.(false);
            resolver.current = resolve;
            setVisible(true);
        }));

        return () => {
            setDisclosureHandler(null);
            settle(false);
        };
    }, [isLoggedIn, settle]);

    useEffect(() => {
        if (!isLoggedIn || !restaurantId) {
            return undefined;
        }

        const run = () => {
            sendAppOpenPing().catch(() => {});
            flushQueue().catch(() => {});
        };

        if (AppState.currentState === 'active') {
            run();
        }

        const sub = AppState.addEventListener('change', (next) => {
            if (next === 'active') {
                run();
            }
        });

        return () => sub?.remove?.();
    }, [isLoggedIn, restaurantId]);

    return (
        <WorkLocationDisclosureSheet
            visible={visible}
            onAllow={() => settle(true)}
            onNotNow={() => settle(false)}
        />
    );
};

export default WorkLocationHost;
